// DETAILS PAGE NAVIGATION, ELEMENT IDS

// https://stackoverflow.com/a/52396706
const mirror = <V extends keyof any>(v: V) => v;

/**
 * @Note
 * this file was primarily meant to act as static values needed for
 * details navigation and filter screen names because of which see the comments below
 */
export const navigationMenuNames = {
  overview: mirror('Overview'),
  goSafe: mirror('goSafe'),
  safetyHygiene: mirror('Safety & Hygiene'),
  rooms: mirror('Room Options'),
  amenities: mirror('Amenities'),
  guestReviews: mirror('Guest Reviews'),
  QnA: mirror('Questions & Answers'),
  hotelPolicies: mirror('Hotel Policies'),
  location: mirror('Location'),
  aboutProperty: mirror('About Property'),
  similarHotels: mirror('Similar Hotels'),
  faqs: mirror('FAQs'),

  /**
   * @TODO
   * srp screen names should live in separate file
   */
  locationFilters: mirror('locationFilters'),
  filters: mirror('filters'),
};

/**
 * @TODO
 * all new screen names should be used from here, so that
 * it's easy to port all of them in a separate file finally
 */
export const srpFilterScreenNames = {
  hotelSearch: mirror('hotelSearch'),
};

const roomsListScreenRouteName = 'details-policy';
const rnrScreenRouteName = 'reviews';
export const routeToScreenMap = {
  [navigationMenuNames.amenities]: mirror('detailAmenitiesInfo'),
  [navigationMenuNames.rooms]: mirror('page-listing'),
  [navigationMenuNames.guestReviews]: mirror(rnrScreenRouteName),
  [navigationMenuNames.hotelPolicies]: mirror(roomsListScreenRouteName),
  [navigationMenuNames.overview]: mirror('overview'),
  roomImagesGallery: mirror(`${roomsListScreenRouteName}/newroomdetails`),
  rnrImageGallery: mirror(`${rnrScreenRouteName}/rnrgallery`),
  roomViewMore: mirror('newroomdetails'),
  [navigationMenuNames.filters]: mirror('filters'),

  /**
   * @TODO
   * srp screen names should live in a separate file
   * */
  [navigationMenuNames.locationFilters]: mirror('locationFilters'),
  [navigationMenuNames.location]: mirror('location'),
};

export const navigationMenuNamesList = Object.values(navigationMenuNames);
export const allDetailsScreens = Object.values(routeToScreenMap);

export type NavigationMenuNamesType = typeof navigationMenuNames[keyof typeof navigationMenuNames];

export type ScreenNamesType = typeof routeToScreenMap[keyof typeof routeToScreenMap];

export const detailsNavigationIdsAndValues = {
  overview: {
    id: '#overview',
    value: navigationMenuNames.overview,
  },
  safetyHygiene: {
    id: '#safetyHygiene',
    value: navigationMenuNames.safetyHygiene,
  },
  goSafe: {
    id: '#go-safe',
    value: navigationMenuNames.goSafe,
  },
  rooms: {
    id: '#rooms',
    value: navigationMenuNames.rooms,
  },
  amenities: {
    id: '#amenities',
    value: navigationMenuNames.amenities,
  },
  guestReviews: {
    id: '#guest-reviews',
    value: navigationMenuNames.guestReviews,
  },
  QnA: {
    id: '#QnA',
    value: navigationMenuNames.QnA,
  },
  hotelPolicies: {
    id: '#hotel-policies',
    value: navigationMenuNames.hotelPolicies,
  },
  location: {
    id: '#location',
    value: navigationMenuNames.location,
  },
  aboutProperty: {
    id: '#about-property',
    value: navigationMenuNames.aboutProperty,
  },
  similarHotels: {
    id: '#similar-hotels',
    value: navigationMenuNames.similarHotels,
  },
  faqs: {
    id: '#faqs',
    value: navigationMenuNames.faqs,
  },
};

type DetailsNavigationIdsType = {
  [K in keyof typeof detailsNavigationIdsAndValues]: string;
};

/* for easy use when ids have to be set in markup, flatten the ids /*
/*  this function returns this  */
/**
 * {
 *    rooms: "rooms",
 *    amenities: "amenities"
 *    …
 * }
 */

export const detailsNavigationIds = Object.keys(detailsNavigationIdsAndValues).reduce(
  (idsAccumulator, currentKey) => {
    return {
      ...idsAccumulator,
      [currentKey]: detailsNavigationIdsAndValues[currentKey].id.replace('#', ''),
    };
  },
  {} as DetailsNavigationIdsType,
);

export const getDetailsNavigationIdsAndValuesObjectById = (id: string) => {
  const key = Object.keys(detailsNavigationIdsAndValues).filter(key => {
    return detailsNavigationIdsAndValues[key].id === id;
  })[0];

  const value = detailsNavigationIdsAndValues[key];

  return value;
};
